// Comprehensive glossary of symbols and notation used throughout PhonX.
export const PHONETIC_SYMBOLS = [
  { symbol: "/a/", meaning: "short A sound", example: "apple, cat, hat" },
  { symbol: "/e/", meaning: "short E sound", example: "egg, bed, net" },
  { symbol: "/i/", meaning: "short I sound", example: "igloo, pig, hit" },
  { symbol: "/o/", meaning: "short O sound", example: "orange, dog, top" },
  { symbol: "/u/", meaning: "short U sound", example: "umbrella, cup, bug" },
  { symbol: "/b/", meaning: "B sound", example: "ball, cab" },
  { symbol: "/k/", meaning: "hard C / K sound", example: "cat, kite, back" },
  { symbol: "/d/", meaning: "D sound", example: "dog, bed" },
  { symbol: "/f/", meaning: "F sound", example: "fish, off" },
  { symbol: "/g/", meaning: "hard G sound", example: "goat, big" },
  { symbol: "/h/", meaning: "H sound", example: "hat, her" },
  { symbol: "/j/", meaning: "J sound", example: "juice, jam" },
  { symbol: "/l/", meaning: "L sound", example: "lion, ball" },
  { symbol: "/m/", meaning: "M sound", example: "moon, sum" },
  { symbol: "/n/", meaning: "N sound", example: "nest, run" },
  { symbol: "/p/", meaning: "P sound", example: "pig, top" },
  { symbol: "/r/", meaning: "R sound", example: "rain, run" },
  { symbol: "/s/", meaning: "S sound", example: "sun, kiss" },
  { symbol: "/t/", meaning: "T sound", example: "tree, hat" },
  { symbol: "/v/", meaning: "V sound", example: "van, love" },
  { symbol: "/w/", meaning: "W sound", example: "web, we" },
  { symbol: "/y/", meaning: "Y as a consonant", example: "yes, yarn" },
  { symbol: "/z/", meaning: "Z sound", example: "zebra, buzz" },
  { symbol: "/kw/", meaning: "the QU blend", example: "queen, quiz" },
  { symbol: "/ks/", meaning: "the X sound", example: "box, fox" },
  { symbol: "/sh/", meaning: "SH digraph", example: "ship, wish" },
  { symbol: "/ch/", meaning: "CH digraph", example: "cheese, chip" },
  { symbol: "/th/", meaning: "TH digraph (unvoiced)", example: "thumb, thin" },
  { symbol: "/ng/", meaning: "NG ending sound", example: "sing, ring" },
  { symbol: "/oo/", meaning: "long OO", example: "moon, boot" },
  { symbol: "/ee/", meaning: "long E", example: "beach, sheep" },
  { symbol: "/ay/", meaning: "long A (AI/AY)", example: "rain, play" },
  { symbol: "/ow/", meaning: "OU / OW diphthong", example: "cloud, cow" },
  { symbol: "/shun/", meaning: "-TION suffix sound", example: "nation, action" },
  { symbol: "/ing/", meaning: "-ING suffix", example: "reading, singing" },
  { symbol: "/off/, /uff/, /oh/", meaning: "OUGH — pronounced differently by word", example: "thought, tough, though" },
];

export const DIGRAPHS = [
  { pattern: "SH", note: "Two letters, one /sh/ sound.", example: "ship" },
  { pattern: "CH", note: "Consonant digraph, unvoiced /ch/.", example: "cheese" },
  { pattern: "TH", note: "Two letters, one dental /th/ sound.", example: "thumb" },
  { pattern: "PH", note: "PH makes the /f/ sound.", example: "phone" },
  { pattern: "WH", note: "Usually /w/, sometimes /h/.", example: "whale" },
  { pattern: "NG", note: "Ending nasal sound.", example: "sing" },
  { pattern: "OO", note: "Long vowel digraph.", example: "moon" },
  { pattern: "EA", note: "Often the long-E sound.", example: "beach" },
  { pattern: "AI", note: "Long-A vowel team.", example: "rain" },
  { pattern: "OU", note: "Diphthong — gliding vowel.", example: "cloud" },
  { pattern: "IGH", note: "Trigraph for long I.", example: "light" },
  { pattern: "TION", note: "Suffix producing /shun/.", example: "nation" },
  { pattern: "OUGH", note: "Context-dependent pronunciation.", example: "thought / tough" },
];

export const SYLLABLE_PATTERNS = [
  { pattern: "VC/CV", note: "Split between two consonants.", example: "RAB|BIT, NAP|KIN" },
  { pattern: "V/CV", note: "Open syllable — the vowel says its name.", example: "TI|GER, PI|LOT" },
  { pattern: "VC/V", note: "Short vowel — closed first syllable.", example: "LEM|ON, WAG|ON" },
  { pattern: "Onset + Rime", note: "Beginning sound (onset) plus the rest (rime).", example: "c + at = cat" },
  { pattern: "CVC", note: "Consonant–Vowel–Consonant — classic short-vowel words.", example: "cat, hop, sit" },
  { pattern: "CVCe", note: "Silent-e makes the vowel long.", example: "cake, kite, tune" },
];

// Which game each launcher icon represents
export const GAME_ICON_KEY = [
  { icon: "🎵 Music note", meaning: "Letter–Sound Match" },
  { icon: "Aa Case letters", meaning: "Case Match" },
  { icon: "🐰 Rabbit", meaning: "Rhyme Hunt" },
  { icon: "👂 Ear", meaning: "First Sound Sort" },
  { icon: "🧩 Puzzle piece", meaning: "Word Building" },
  { icon: "🖼️ Image frame", meaning: "Picture to Word" },
  { icon: "🔀 Combine arrows", meaning: "Blending Studio" },
  { icon: "🔁 Repeat arrows", meaning: "Word Families" },
  { icon: "✂️ Scissors", meaning: "Syllable Division" },
  { icon: "📖 Open book", meaning: "Vocab in Context" },
  { icon: "📃 List tree", meaning: "Chunk & Prosody" },
  { icon: "Aa Type", meaning: "Sentence Puzzles" },
  { icon: "📋 Clipboard", meaning: "Adult Phonics Quiz" },
];

// Emojis learners will see on lesson cards
export const LESSON_EMOJI_KEY = [
  { emoji: "🍎", meaning: "Apple — A", },
  { emoji: "⚽", meaning: "Ball — B" },
  { emoji: "🐱", meaning: "Cat — C" },
  { emoji: "🐶", meaning: "Dog — D" },
  { emoji: "🥚", meaning: "Egg — E" },
  { emoji: "🐟", meaning: "Fish — F" },
  { emoji: "🐐", meaning: "Goat — G" },
  { emoji: "🎩", meaning: "Hat — H" },
  { emoji: "🧊", meaning: "Igloo — I" },
  { emoji: "🧃", meaning: "Juice — J" },
  { emoji: "🪁", meaning: "Kite — K" },
  { emoji: "🦁", meaning: "Lion — L" },
  { emoji: "🌙", meaning: "Moon — M/N" },
  { emoji: "🪺", meaning: "Nest — N" },
  { emoji: "🍊", meaning: "Orange — O" },
  { emoji: "🐷", meaning: "Pig — P" },
  { emoji: "👑", meaning: "Queen — Q" },
  { emoji: "🌧️", meaning: "Rain — R" },
  { emoji: "☀️", meaning: "Sun — S" },
  { emoji: "🌳", meaning: "Tree — T" },
  { emoji: "☂️", meaning: "Umbrella — U" },
  { emoji: "🚐", meaning: "Van — V" },
  { emoji: "🕸️", meaning: "Web — W" },
  { emoji: "📦", meaning: "Box — X" },
  { emoji: "🧶", meaning: "Yarn — Y" },
  { emoji: "🦓", meaning: "Zebra — Z" },
  { emoji: "🚢", meaning: "Ship — SH digraph" },
  { emoji: "🧀", meaning: "Cheese — CH digraph" },
  { emoji: "👍", meaning: "Thumb — TH digraph" },
  { emoji: "📞", meaning: "Phone — PH digraph" },
  { emoji: "🏖️", meaning: "Beach — EA vowel team" },
  { emoji: "☁️", meaning: "Cloud — OU diphthong" },
  { emoji: "💡", meaning: "Light — IGH trigraph" },
  { emoji: "🌍", meaning: "Nation — TION suffix" },
  { emoji: "💭", meaning: "Thought — OUGH pattern" },
];

// Punctuation marks — the marks used when writing sentences.
export const PUNCTUATION = [
  { symbol: ".", name: "Period (full stop)", meaning: "Ends a normal sentence.", example: "I like books." },
  { symbol: ",", name: "Comma", meaning: "A short pause; separates items in a list or clauses.", example: "I have apples, pears, and grapes." },
  { symbol: "?", name: "Question mark", meaning: "Ends a question.", example: "Where are you going?" },
  { symbol: "!", name: "Exclamation mark", meaning: "Shows strong feeling — surprise, excitement, or a command.", example: "Look out!" },
  { symbol: ":", name: "Colon", meaning: "Introduces a list, explanation, or quote.", example: "Bring these: pens, paper, and a ruler." },
  { symbol: ";", name: "Semicolon", meaning: "Links two closely related sentences.", example: "It was late; we went home." },
  { symbol: "\u201C \u201D", name: "Quotation marks", meaning: "Wrap the exact words someone said or a title.", example: "She said, \u201CHello.\u201D" },
  { symbol: "\u2018 \u2019", name: "Single quotes", meaning: "Quotes inside quotes, or (UK) direct speech.", example: "She said, \u201CI heard him say \u2018hi.\u2019\u201D" },
  { symbol: "\u2019", name: "Apostrophe", meaning: "Shows possession or a missing letter (contraction).", example: "Ben\u2019s book; don\u2019t = do not" },
  { symbol: "( )", name: "Parentheses / round brackets", meaning: "Add extra, non-essential information.", example: "My cat (a tabby) is asleep." },
  { symbol: "[ ]", name: "Square brackets", meaning: "Add editor notes inside a quote.", example: "\u201CShe [Anna] left early.\u201D" },
  { symbol: "-", name: "Hyphen", meaning: "Joins words or parts of words.", example: "well-known, twenty-one" },
  { symbol: "\u2013", name: "En dash", meaning: "Shows a range between numbers or dates.", example: "pages 10\u201320; 2020\u20132024" },
  { symbol: "\u2014", name: "Em dash", meaning: "A stronger break in a sentence \u2014 like this \u2014 for emphasis.", example: "The answer \u2014 finally \u2014 was yes." },
  { symbol: "\u2026", name: "Ellipsis", meaning: "Shows a pause, trailing thought, or omitted text.", example: "I was going to\u2026 never mind." },
  { symbol: "/", name: "Slash", meaning: "Means \u201cor\u201D, or separates parts (dates, fractions).", example: "and/or; 12/05/2025" },
  { symbol: "&", name: "Ampersand", meaning: "Means \u201cand\u201D \u2014 mostly used in names or short labels.", example: "Rock & Roll; Q & A" },
  { symbol: "@", name: "At sign", meaning: "Means \u201cat\u201D \u2014 used in emails and pricing.", example: "chris@example.com; 3 @ $2 each" },
  { symbol: "#", name: "Hash / pound", meaning: "Marks a number or a topic (hashtag).", example: "Room #5; #reading" },
  { symbol: "*", name: "Asterisk", meaning: "Points to a footnote or highlights emphasis.", example: "Free shipping* (*over $20)" },
];

// General notation used in the app
export const APP_NOTATION = [
  { symbol: "/…/", meaning: "Slashes wrap a phonetic sound — the way a letter or letter-group is pronounced." },
  { symbol: "•••", meaning: "Placeholder dots in Blending Studio — hidden letters revealed after blending." },
  { symbol: "|", meaning: "Syllable break in the Syllable Division game (e.g. RAB|BIT)." },
  { symbol: "…", meaning: "In taglines like 'c…a…t → cat', represents slowed sound-by-sound blending." },
  { symbol: "→", meaning: "Arrow shows a transformation: swapping an onset (c → b in the -at family)." },
  { symbol: "✅ / ❌", meaning: "Green tile = correct pick, red tile shake = try again." },
];
